import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import GlobalContext from '../../../../../contexts/GlobalContext';
import { AccountContext } from '../../../../../components/apps/account/context/AccountProvider';
import IFormSubmitResult from '../../../../../interfaces/IFormSubmitResult';
import submitForm, { FailedResponse, submitRequest, SuccessResponse } from '../../../../../utilities/submitForm';
import { ITeamMember } from '../interfaces/ITeamMember';
import { IDepartmentMember } from '../interfaces/IDepartmentMember';
import { IPersonalNoteDto, ITeamNotes } from '../interfaces/IPersonalNote';
import { IParhMember } from '../interfaces/IParhMember';
import LocalizationContext from 'src/contexts/LocalizationContext';
import SubAppsEnum from 'src/enums/SubAppsEnum';
import IMFilesObject from 'src/interfaces/IMFilesObject';
import { IPersonalNoteAttachedFile } from 'src/interfaces/IAttachedFile';
import { IReferenceEntityDto } from '../../common/IReferenceEntityDto';

interface TeamContextData extends TeamContextState {
    isManager: boolean;
    isPARH: boolean;
    openProfile(profileId: number, viewingSubAppName: string, viewingSubAppId: number): void;
    closeProfile(subAppId: number): void;

    loadTeamMembers(
        consultingYear: number,
        viewingSubApp: IMFilesObject,
        success: (result: { teamMembers: ITeamMember[]; departmentMembers: IDepartmentMember[]; parhMembers: IParhMember[] }) => void,
        failed: FailedResponse
    ): void;
    loadEmployeePicture(employeeId: number, onComplete: (source: string) => void): void;

    loadPersonalNotes(year: string, success: (notes: IPersonalNoteDto[]) => void, failed: FailedResponse): void;
    addPersonalNote(note: string, criteriaId: number, files: File[], success: (noteId: number) => void, failed: FailedResponse): void;
    editPersonalNote(
        noteId: number,
        note: string,
        criteriaId: number,
        success: (note: IPersonalNoteDto) => void,
        failed: FailedResponse
    ): void;
    deletePersonalNote(noteId: number, success: SuccessResponse, failed: FailedResponse): void;
    loadAttachments(noteId: number, onComplete: (files: IPersonalNoteAttachedFile[]) => void): void;
    addAttachments(noteId: number, files: File[], success: (result: IPersonalNoteAttachedFile[]) => void, failed: FailedResponse): void;
    deleteAttachment(noteId: number, fileId: number, success: SuccessResponse, failed: FailedResponse): void;
    setSubAppValues(viewingSubApp: IMFilesObject): void;
}

interface TeamContextState {
    profileId: number;

    viewingSubApps: IMFilesObject;

    objectiveCompleted: number;
    objectiveTotal: number;

    firstFollowupCompleted: number;
    firstFollowupTotal: number;

    secondFollowupCompleted: number;
    secondFollowupTotal: number;

    careerCompleted: number;
    careerTotal: number;

    feedbackCompleted: number;
    feedbackTotal: number;

    overallAssessmentCompleted: number;
    overallAssessmentTotal: number;

    dateLastUpdated: Date;
    dateRefresh: Date;

    viewingProfile?: ITeamMember;
    teamMembers: ITeamMember[];
    departmentMembers: IDepartmentMember[];
    parhMembers: IParhMember[];
    notes: IPersonalNoteDto[];
    performanceCriterias: IReferenceEntityDto[];
    loading: boolean;
    profilePicture: string;
}

interface TeamProviderProps {
    children: ReactNode;
}

const defaultTeamData: TeamContextState = {
    profileId: 0,
    viewingSubApps: { id: 0, name: SubAppsEnum.team },
    viewingProfile: undefined,
    objectiveCompleted: 0,
    objectiveTotal: 0,

    firstFollowupCompleted: 0,
    firstFollowupTotal: 0,

    secondFollowupCompleted: 0,
    secondFollowupTotal: 0,

    careerCompleted: 0,
    careerTotal: 0,

    feedbackCompleted: 0,
    feedbackTotal: 0,

    overallAssessmentCompleted: 0,
    overallAssessmentTotal: 0,

    dateRefresh: new Date(),
    dateLastUpdated: new Date(),

    teamMembers: [],
    departmentMembers: [],
    parhMembers: [],
    notes: [],
    performanceCriterias: [],

    loading: false,
    profilePicture: '/images/empty-profile.jpg',
};

export const TeamContext = createContext<TeamContextData>({
    ...defaultTeamData,
    isManager: false,
    isPARH: false,
    openProfile: () => {},
    closeProfile: () => {},
    loadTeamMembers: () => {},
    loadEmployeePicture: () => {},
    loadPersonalNotes: () => {},
    addPersonalNote: () => {},
    editPersonalNote: () => {},
    deletePersonalNote: () => {},
    loadAttachments: () => {},
    addAttachments: () => {},
    deleteAttachment: () => {},
    setSubAppValues: () => {},
});

const TeamProvider = ({ children }: TeamProviderProps) => {
    const global = useContext(GlobalContext);
    const account = useContext(AccountContext);

    const localization = useContext(LocalizationContext);
    const [state, setState] = useState<TeamContextState>(defaultTeamData);

    const loadTeamMembers = useCallback(
        (
            consultingYear: number,
            viewingSubApp: IMFilesObject,
            success: (result: { teamMembers: ITeamMember[]; departmentMembers: IDepartmentMember[]; parhMembers: IParhMember[] }) => void,
            failed: FailedResponse
        ) => {
            setState((s: TeamContextState) => ({ ...s, loading: true }));
            submitRequest(
                `/api/edp/team?consultingYear=${consultingYear}`,
                undefined,
                undefined,
                undefined,
                (result: {
                    teamMembers: ITeamMember[];
                    departmentMembers: IDepartmentMember[];
                    parhMembers: IParhMember[];
                    objectiveCompleted: number;
                    objectiveTotal: number;

                    firstFollowupCompleted: number;
                    firstFollowupTotal: number;

                    secondFollowupCompleted: number;
                    secondFollowupTotal: number;

                    careerCompleted: number;
                    careerTotal: number;

                    feedbackCompleted: number;
                    feedbackTotal: number;

                    dateRefresh: Date;
                    dateLastUpdated: Date;

                    overallAssessmentCompleted: number;
                    overallAssessmentTotal: number;
                }) => {
                    setState((s: TeamContextState) => ({
                        ...s,
                        loading: false,
                        ...result,
                        viewingSubApps: { ...s.viewingSubApps, id: viewingSubApp.id, name: viewingSubApp.name },
                    }));
                    success(result);
                },
                (result: IFormSubmitResult) => {
                    setState((s: TeamContextState) => ({
                        ...s,
                        loading: false,
                        viewingSubApps: { id: 0, name: SubAppsEnum.team },
                        teamMembers: [],
                        departmentMembers: [],
                        parhMembers: [],
                        objectiveCompleted: 0,
                        objectiveTotal: 0,

                        followupCompleted: 0,
                        followupTotal: 0,

                        careerCompleted: 0,
                        careerTotal: 0,

                        feedbackCompleted: 0,
                        feedbackTotal: 0,

                        dateRefresh: new Date(),
                        dateLastUpdated: new Date(),

                        overallAssessmentCompleted: 0,
                        overallAssessmentTotal: 0,
                    }));
                    failed(result);
                }
            );
        },
        []
    );

    const loadEmployeePicture = useCallback(
        (employeeId: number, onComplete: (source: string) => void) => {
            const defaultImg: string = '/images/empty-profile.jpg';
            fetch(`api/employee/get-picture/${employeeId}`).then((r) => {
                if (r.ok)
                    r.text()
                        .then((o: string) => {
                            setState((s: TeamContextState) => ({ ...s, profilePicture: o ?? defaultImg }));
                            onComplete(o ?? defaultImg);
                        })
                        .catch(() => {
                            setState((s: TeamContextState) => ({ ...s, profilePicture: defaultImg }));
                            onComplete(defaultImg);
                        });
                else {
                    setState((s: TeamContextState) => ({ ...s, profilePicture: defaultImg }));
                    onComplete(defaultImg);
                }
            });
        },
        [setState]
    );

    const openProfile = useCallback(
        (profileId: number, viewingSubAppName: string, viewingSubAppId: number) => {
            const userInfo =
                profileId === 0
                    ? undefined
                    : state.teamMembers?.find((m: ITeamMember) => m.id === profileId) ??
                      state.departmentMembers?.find((m: IDepartmentMember) => m.id === profileId) ??
                      state.parhMembers?.find((m: IParhMember) => m.id === profileId);

            setState((s: TeamContextState) => ({
                ...s,
                profileId,
                viewingSubApps: { ...s.viewingSubApps, id: viewingSubAppId, name: viewingSubAppName },
                viewingProfile: userInfo,
                profilePicture: profileId === 0 ? '/images/empty-profile.jpg' : s.profilePicture,
            }));

            if (profileId !== 0 && userInfo) loadEmployeePicture(userInfo.id, () => {});
        },

        [loadEmployeePicture, state.departmentMembers, state.parhMembers, state.teamMembers]
    );

    const loadPersonalNotes = useCallback(
        (year: string, success: (result: IPersonalNoteDto[]) => void, failed: FailedResponse) => {
            submitRequest(
                `/api/edp/team/notes?employeeId=${state.profileId}&year=${year}`,
                undefined,
                undefined,
                undefined,
                (teamNotes: ITeamNotes) => {
                    setState((s: TeamContextState) => ({
                        ...s,
                        notes: [...teamNotes.personalNotes],
                        performanceCriterias: teamNotes.performanceCriterias,
                    }));

                    success([...teamNotes.personalNotes]);
                },
                failed
            );
        },
        [state.profileId]
    );

    const addPersonalNote = useCallback(
        (note: string, criteriaId: number, files: File[], success: (noteId: number) => void, failed: FailedResponse) => {
            submitForm(
                `/api/edp/team/notes`,
                {
                    forceFormData: true,
                    employeeId: state.profileId,
                    note,
                    criteriaId: criteriaId,
                    year: global.consultingYear ?? new Date().getFullYear(),
                },
                files,
                global,
                account,
                (newPersonalNoteDto: IPersonalNoteDto) => {
                    setState((s: TeamContextState) => ({
                        ...s,
                        notes: [...s.notes, newPersonalNoteDto],
                    }));

                    success(newPersonalNoteDto.id);
                },
                failed
            );
        },
        [state.profileId, global, account]
    );

    const editPersonalNote = useCallback(
        (noteId: number, note: string, criteriaId: number, success: (note: IPersonalNoteDto) => void, failed: FailedResponse) => {
            submitRequest(
                `/api/edp/team/notes/${noteId}`,
                { noteId, note, criteriaId },
                global,
                undefined,
                (note: IPersonalNoteDto) => {
                    setState((s: TeamContextState) => {
                        let updateNotes = [...s.notes];

                        updateNotes.splice(
                            s.notes.findIndex((o: IPersonalNoteDto) => o.id === note.id),
                            1,
                            { ...note }
                        );

                        return {
                            ...s,
                            notes: updateNotes,
                        };
                    });
                    success(note);
                },
                failed,
                'PUT'
            );
        },
        [global, setState]
    );

    const deletePersonalNote = useCallback(
        (noteId: number, success: SuccessResponse, failed: FailedResponse) => {
            submitRequest(
                `/api/edp/team/notes/${noteId}`,
                undefined,
                undefined,
                undefined,
                (result: IFormSubmitResult) => {
                    setState((s: TeamContextState) => ({
                        ...s,
                        notes: s.notes.filter((n) => n.id !== noteId),
                    }));
                    success(result);
                },
                failed,
                'DELETE'
            );
        },
        [setState]
    );

    const addAttachments = useCallback(
        (noteId: number, files: File[], success: (result: IPersonalNoteAttachedFile[]) => void, failed: FailedResponse) => {
            submitForm<IPersonalNoteAttachedFile[]>(
                `/api/edp/team/notes/attachments/${noteId}`,
                undefined,
                files,
                global,
                account,
                (result: IPersonalNoteAttachedFile[]) => {
                    const notes = state.notes;
                    const currentNote = notes.find((n) => n.id === noteId);
                    if (currentNote) {
                        currentNote.files = [...(currentNote.files || []), ...result];
                    }

                    setState((s: TeamContextState) => ({
                        ...s,
                        notes,
                        loading: false,
                    }));

                    success(result);
                },
                failed
            );
        },
        [state.notes, global, account]
    );

    const deleteAttachment = useCallback(
        (noteId: number, fileId: number, success: SuccessResponse, failed: FailedResponse) => {
            submitRequest(
                `/api/edp/team/notes/attachments/${noteId}/${fileId}`,
                undefined,
                undefined,
                undefined,
                (result: IFormSubmitResult) => {
                    const notes = state.notes;
                    const currentNote = notes.find((n) => n.id === noteId);
                    if (currentNote) {
                        currentNote.files = currentNote?.files?.filter((f) => f.id !== fileId);
                    }

                    setState((s: TeamContextState) => ({
                        ...s,
                        notes,
                        loading: false,
                    }));

                    success(result);
                },
                failed,
                'DELETE'
            );
        },
        [state.notes]
    );

    const loadAttachments = useCallback(
        (noteId: number, onComplete: (noteFiles: IPersonalNoteAttachedFile[]) => void) => {
            fetch(`/api/edp/team/notes/attachments/${noteId}`)
                .then((res) => (res.ok ? res.json() : Promise.reject(res)))
                .then((result: IPersonalNoteAttachedFile[]) => {
                    onComplete(result);

                    var noteToUpdate = state.notes.find((n) => n.id === noteId);

                    if (noteToUpdate) {
                        noteToUpdate.files = result;
                    }

                    setState((s: TeamContextState) => ({
                        ...s,
                        loading: false,
                    }));
                })
                .catch(() => {
                    setState((s: TeamContextState) => ({ ...s, loading: false }));
                    alert(localization.strings.loadFilesError);
                });
        },
        [localization.strings.loadFilesError, state.notes]
    );

    const setSubAppValues = useCallback((viewingSubApp: IMFilesObject) => {
        setState((s: TeamContextState) => ({
            ...s,
            viewingSubApps: viewingSubApp,
        }));
    }, []);

    const context = useMemo<TeamContextData>(
        () => ({
            ...state,
            isManager: true,
            isPARH: state.parhMembers.length > 0,
            loadTeamMembers,
            loadEmployeePicture,
            openProfile,
            closeProfile: (subAppId) => openProfile(0, SubAppsEnum.team, subAppId),
            loadPersonalNotes,
            addPersonalNote,
            editPersonalNote,
            deletePersonalNote,
            loadAttachments,
            addAttachments,
            deleteAttachment,
            setSubAppValues,
        }),
        [
            state,
            loadTeamMembers,
            loadEmployeePicture,
            openProfile,
            loadPersonalNotes,
            addPersonalNote,
            editPersonalNote,
            deletePersonalNote,
            loadAttachments,
            addAttachments,
            deleteAttachment,
            setSubAppValues,
        ]
    );

    return <TeamContext.Provider value={context}>{children}</TeamContext.Provider>;
};

export default TeamProvider;
